import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { lazy, Suspense, useState } from "react";

import { api } from "../../../lib/api";

const ManagersChart = lazy(() => import("./-ManagersCharts"));

function ChartFallback() {
  return <div style={{ padding: 24, fontSize: 12, color: "var(--ink-dim)" }}>Carregando gráfico…</div>;
}

export const Route = createFileRoute("/_authed/reports/managers")({
  component: ManagersReportPage,
});

// ── tipos ────────────────────────────────────────────────────────────────────

interface ManagerRow {
  id: string;
  name: string;
  agents: { total: number; active: number; development: number; inactive: number };
  health: { ok: number; attention: number; zombie: number };
  cost_usd: number;
  executions: number;
  errors: number;
  error_rate: number;
  tokens: number;
  qualified_leads: number;
}

interface ManagersReport {
  managers: ManagerRow[];
  totals: { agents: number; ok: number; attention: number; zombie: number; cost_usd: number; executions: number; errors: number; qualified_leads: number };
}

interface AgentRow {
  id: string;
  name: string;
  status: string;
  health: "ok" | "attention" | "zombie" | "none";
  health_reason: string | null;
  last_exec_at: string | null;
  cost_usd: number;
  executions: number;
  errors: number;
  error_rate: number;
  tokens: number;
  qualified_leads: number;
  prompt_edits: number;
}

interface ManagerDetail {
  manager: { id: string; name: string };
  health: { ok: number; attention: number; zombie: number; none: number };
  agents: AgentRow[];
  series: { day: string; cost_usd: number; executions: number; errors: number }[];
  qualified_leads: number;
  cost_usd: number;
}

// ── helpers ──────────────────────────────────────────────────────────────────

const PRESET_PERIODS = [
  { label: "24h", hours: 24 },
  { label: "7d",  hours: 168 },
  { label: "30d", hours: 720 },
  { label: "90d", hours: 2160 },
  { label: "6m",  hours: 4320 },
  { label: "12m", hours: 8760 },
];

function fmtCost(v: number) {
  return `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: v < 1 ? 4 : 2 })}`;
}

function fmtInt(v: number) {
  return v.toLocaleString("pt-BR");
}

function fmtAgo(iso: string | null) {
  if (!iso) return "30d+";
  const h = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
  if (h < 1) return "<1h";
  return h < 48 ? `${h}h` : `${Math.floor(h / 24)}d`;
}

const HEALTH: Record<string, { label: string; color: string }> = {
  ok:        { label: "Saudável", color: "var(--ok)" },
  attention: { label: "Atenção",  color: "var(--warn)" },
  zombie:    { label: "Zumbi",    color: "var(--danger)" },
  none:      { label: "—",        color: "var(--ink-muted)" },
};

const STATUS_LABEL: Record<string, string> = {
  active: "Ativo", development: "Desenv.", inactive: "Inativo",
};

// ── período ──────────────────────────────────────────────────────────────────

function usePeriod() {
  const [presetHours, setPresetHours] = useState(720);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const usingRange = !!(fromDate && toDate);
  const queryParams = usingRange
    ? `from_ts=${new Date(fromDate).toISOString()}&until_ts=${new Date(toDate + "T23:59:59").toISOString()}`
    : `hours=${presetHours}`;
  const label = usingRange
    ? `${fromDate} → ${toDate}`
    : `Últimos ${PRESET_PERIODS.find(p => p.hours === presetHours)?.label ?? `${presetHours}h`}`;
  return { presetHours, setPresetHours, fromDate, setFromDate, toDate, setToDate, usingRange, queryParams, label,
    clearRange: () => { setFromDate(""); setToDate(""); } };
}

function PeriodSelector({ p }: { p: ReturnType<typeof usePeriod> }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      <div style={{ display: "flex", gap: 6 }}>
        {PRESET_PERIODS.map(pr => (
          <button key={pr.hours} onClick={() => { p.setPresetHours(pr.hours); p.clearRange(); }}
            style={{
              padding: "5px 12px", borderRadius: 6, fontSize: 12, cursor: "pointer",
              border: `1px solid ${!p.usingRange && p.presetHours === pr.hours ? "var(--violet-alt)" : "var(--line-strong)"}`,
              background: !p.usingRange && p.presetHours === pr.hours ? "rgba(108,71,255,0.15)" : "var(--surface-input)",
              color: !p.usingRange && p.presetHours === pr.hours ? "var(--violet)" : "var(--ink-mid)",
            }}
          >{pr.label}</button>
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>De</span>
        <input type="date" value={p.fromDate} onChange={e => p.setFromDate(e.target.value)}
          style={{ background: "var(--surface-input)", border: `1px solid ${p.usingRange ? "var(--violet-alt)" : "var(--line-strong)"}`, borderRadius: 6, padding: "5px 10px", fontSize: 12, color: "var(--ink)" }} />
        <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>até</span>
        <input type="date" value={p.toDate} onChange={e => p.setToDate(e.target.value)}
          style={{ background: "var(--surface-input)", border: `1px solid ${p.usingRange ? "var(--violet-alt)" : "var(--line-strong)"}`, borderRadius: 6, padding: "5px 10px", fontSize: 12, color: "var(--ink)" }} />
        {p.usingRange && (
          <button onClick={p.clearRange} style={{ fontSize: 11, color: "var(--ink-muted)", padding: "3px 8px", borderRadius: 4, background: "none", border: "none", cursor: "pointer" }}>
            Limpar
          </button>
        )}
      </div>
    </div>
  );
}

// ── blocos visuais ───────────────────────────────────────────────────────────

function TotalsCards({ cards }: { cards: { label: string; value: string; color: string }[] }) {
  return (
    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
      {cards.map(c => (
        <div key={c.label} style={{ flex: 1, minWidth: 130, background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 8, padding: "14px 18px" }}>
          <div style={{ fontSize: 11, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 6 }}>{c.label}</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: c.color }}>{c.value}</div>
        </div>
      ))}
    </div>
  );
}

function HealthSummary({ ok, attention, zombie }: { ok: number; attention: number; zombie: number }) {
  return (
    <span style={{ fontSize: 12, textAlign: "right", whiteSpace: "nowrap" }}>
      <span style={{ color: "var(--ok)", fontWeight: 600 }}>{ok}</span>
      <span style={{ color: "var(--ink-dim)" }}> / </span>
      <span style={{ color: attention > 0 ? "var(--warn)" : "var(--ink-muted)", fontWeight: 600 }}>{attention}</span>
      <span style={{ color: "var(--ink-dim)" }}> / </span>
      <span style={{ color: zombie > 0 ? "var(--danger)" : "var(--ink-muted)", fontWeight: 600 }}>{zombie}</span>
    </span>
  );
}

function SectionCard({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 8, padding: "14px 18px 16px" }}>
      <div style={{ fontSize: 11, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 10 }}>
        {title}{subtitle && <span style={{ textTransform: "none", letterSpacing: 0 }}> — {subtitle}</span>}
      </div>
      {children}
    </div>
  );
}

// ── drill-down de um gestor ──────────────────────────────────────────────────

function ManagerDetailView({ managerId, queryParams, periodLabel, onBack }: {
  managerId: string; queryParams: string; periodLabel: string; onBack: () => void;
}) {
  const { data, isLoading } = useQuery<ManagerDetail>({
    queryKey: ["report-manager-detail", managerId, queryParams],
    queryFn: () => api.get(`/reports/managers/${managerId}?${queryParams}`),
  });

  if (isLoading) return <span style={{ fontSize: 13, color: "var(--ink-dim)" }}>Carregando...</span>;
  if (!data) return null;

  const problem = data.agents.filter(a => a.health === "zombie" || a.health === "attention");

  // Pareto: top 12 por custo + % acumulado sobre o total da carteira
  const byCost = [...data.agents].sort((a, b) => b.cost_usd - a.cost_usd);
  let acc = 0;
  const pareto = byCost.slice(0, 12).map(a => {
    acc += a.cost_usd;
    return { name: a.name.length > 14 ? a.name.slice(0, 13) + "…" : a.name, cost_usd: a.cost_usd,
      cum_pct: data.cost_usd > 0 ? Math.round(acc / data.cost_usd * 100) : 0 };
  });

  const scatterData = data.agents.filter(a => a.cost_usd > 0 || a.qualified_leads > 0)
    .map(a => ({ x: a.cost_usd, y: a.qualified_leads, name: a.name, health: a.health }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={onBack} style={{ background: "var(--surface-input)", border: "1px solid var(--line-strong)", borderRadius: 6, padding: "5px 12px", fontSize: 12, color: "var(--ink-mid)", cursor: "pointer" }}>
          ← Voltar
        </button>
        <span style={{ fontSize: 15, fontWeight: 600 }}>{data.manager.name}</span>
        <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>{periodLabel}</span>
      </div>

      <TotalsCards cards={[
        { label: "Saudáveis",    value: fmtInt(data.health.ok), color: "var(--ok)" },
        { label: "Em atenção",   value: fmtInt(data.health.attention), color: data.health.attention > 0 ? "var(--warn)" : "var(--ink-muted)" },
        { label: "Zumbis",       value: fmtInt(data.health.zombie), color: data.health.zombie > 0 ? "var(--danger)" : "var(--ink-muted)" },
        { label: "Custo",        value: fmtCost(data.cost_usd), color: "var(--ink)" },
        { label: "Leads Qualif.", value: fmtInt(data.qualified_leads), color: "var(--ok)" },
      ]} />

      {/* 1. Lista de ação — o que olhar hoje */}
      <SectionCard title={`Precisa de atenção (${problem.length})`} subtitle="zumbi = ativo sem execução real há 48h · atenção = erro > 5%">
        {problem.length === 0 ? (
          <div style={{ fontSize: 13, color: "var(--ok)", padding: "6px 0" }}>Carteira em dia — nenhum projeto exigindo ação.</div>
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 90px 1.2fr 90px 110px 70px", gap: 8, padding: "6px 0", borderBottom: "1px solid var(--line)" }}>
              {["Projeto", "Saúde", "Motivo", "Últ. exec", "Custo", "Leads"].map((h, i) => (
                <span key={h} style={{ fontSize: 11, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em", textAlign: i > 2 ? "right" : "left" }}>{h}</span>
              ))}
            </div>
            {problem.map(a => (
              <div key={a.id} style={{ display: "grid", gridTemplateColumns: "1fr 90px 1.2fr 90px 110px 70px", gap: 8, padding: "9px 0", borderBottom: "1px solid var(--surface-input)", alignItems: "center" }}>
                <span style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a.name}>{a.name}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: HEALTH[a.health].color }}>{HEALTH[a.health].label}</span>
                <span style={{ fontSize: 12, color: "var(--ink-secondary)" }}>{a.health_reason}</span>
                <span style={{ fontSize: 12, textAlign: "right", color: "var(--ink-muted)" }}>{fmtAgo(a.last_exec_at)}</span>
                <span style={{ fontSize: 13, textAlign: "right" }}>{fmtCost(a.cost_usd)}</span>
                <span style={{ fontSize: 13, textAlign: "right" }}>{fmtInt(a.qualified_leads)}</span>
              </div>
            ))}
          </>
        )}
      </SectionCard>

      {/* 2. Concentração de custo */}
      <SectionCard title="Concentração de custo (Pareto)" subtitle="top 12 projetos + % acumulado da carteira">
        {pareto.length === 0 || data.cost_usd === 0 ? (
          <div style={{ fontSize: 12, color: "var(--ink-dim)", padding: "16px 0", textAlign: "center" }}>Sem custo no período</div>
        ) : (
          <Suspense fallback={<ChartFallback />}>
            <ManagersChart type="pareto" data={pareto} />
          </Suspense>
        )}
      </SectionCard>

      {/* 3. Custo × resultado */}
      <SectionCard title="Custo × Leads por projeto" subtitle="canto inferior direito = caro e não qualifica">
        {scatterData.length === 0 ? (
          <div style={{ fontSize: 12, color: "var(--ink-dim)", padding: "16px 0", textAlign: "center" }}>Sem dados no período</div>
        ) : (
          <Suspense fallback={<ChartFallback />}>
            <ManagersChart type="scatter" data={scatterData} />
          </Suspense>
        )}
      </SectionCard>

      {/* 4. Carteira completa */}
      <SectionCard title={`Carteira completa (${data.agents.length})`} subtitle="ordenada por gravidade e custo">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 80px 80px 100px 80px 65px 60px 60px 75px", gap: 8, padding: "6px 0", borderBottom: "1px solid var(--line)" }}>
          {["Projeto", "Status", "Saúde", "Custo", "Exec", "Erro%", "Leads", "Prompt", "Últ. exec"].map((h, i) => (
            <span key={h} style={{ fontSize: 11, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em", textAlign: i === 0 ? "left" : "right" }}>{h}</span>
          ))}
        </div>
        {data.agents.map(a => (
          <div key={a.id} style={{ display: "grid", gridTemplateColumns: "1fr 80px 80px 100px 80px 65px 60px 60px 75px", gap: 8, padding: "8px 0", borderBottom: "1px solid var(--surface-input)", alignItems: "center" }}>
            <span style={{ fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a.health_reason ?? a.name}>{a.name}</span>
            <span style={{ fontSize: 11, textAlign: "right", color: "var(--ink-muted)" }}>{STATUS_LABEL[a.status] ?? a.status}</span>
            <span style={{ fontSize: 11, fontWeight: 700, textAlign: "right", color: HEALTH[a.health].color }}>{HEALTH[a.health].label}</span>
            <span style={{ fontSize: 13, fontWeight: 600, textAlign: "right" }}>{fmtCost(a.cost_usd)}</span>
            <span style={{ fontSize: 13, textAlign: "right" }}>{fmtInt(a.executions)}</span>
            <span style={{ fontSize: 13, textAlign: "right", color: a.error_rate > 5 ? "var(--danger)" : "var(--ink-dim)" }}>{a.error_rate}%</span>
            <span style={{ fontSize: 13, textAlign: "right" }}>{fmtInt(a.qualified_leads)}</span>
            <span style={{ fontSize: 13, textAlign: "right", color: a.prompt_edits === 0 ? "var(--ink-dim)" : "var(--ink)" }}>{a.prompt_edits}</span>
            <span style={{ fontSize: 12, textAlign: "right", color: "var(--ink-muted)" }}>{fmtAgo(a.last_exec_at)}</span>
          </div>
        ))}
      </SectionCard>

      {/* 5. Série diária (secundária) */}
      <SectionCard title="Custo e Execuções por Dia">
        {data.series.length === 0 ? (
          <div style={{ fontSize: 12, color: "var(--ink-dim)", padding: "16px 0", textAlign: "center" }}>Sem execuções no período</div>
        ) : (
          <Suspense fallback={<ChartFallback />}>
            <ManagersChart type="series" data={data.series} />
          </Suspense>
        )}
      </SectionCard>
    </div>
  );
}

// ── página principal ─────────────────────────────────────────────────────────

function ManagersReportPage() {
  const p = usePeriod();
  const [selected, setSelected] = useState<string | null>(null);

  const { data, isLoading } = useQuery<ManagersReport>({
    queryKey: ["report-managers", p.queryParams],
    queryFn: () => api.get(`/reports/managers?${p.queryParams}`),
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 1100 }}>
      <h1 style={{ position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0 }}>Relatório de gestores</h1>
      <PeriodSelector p={p} />

      {selected ? (
        <ManagerDetailView managerId={selected} queryParams={p.queryParams} periodLabel={p.label} onBack={() => setSelected(null)} />
      ) : (
        <>
          {data && (
            <TotalsCards cards={[
              { label: "Projetos",     value: fmtInt(data.totals.agents), color: "var(--ink)" },
              { label: "Saudáveis",    value: fmtInt(data.totals.ok), color: "var(--ok)" },
              { label: "Em atenção",   value: fmtInt(data.totals.attention), color: data.totals.attention > 0 ? "var(--warn)" : "var(--ink-muted)" },
              { label: "Zumbis",       value: fmtInt(data.totals.zombie), color: data.totals.zombie > 0 ? "var(--danger)" : "var(--ink-muted)" },
              { label: "Custo Total",  value: fmtCost(data.totals.cost_usd), color: "var(--ink)" },
              { label: "Leads Qualif.", value: fmtInt(data.totals.qualified_leads), color: "var(--ok)" },
            ]} />
          )}

          <SectionCard title="Gestores" subtitle={`${p.label} · saúde = saudáveis / atenção / zumbis · clique para detalhar`}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 90px 110px 110px 90px 65px 65px", gap: 8, padding: "6px 0", borderBottom: "1px solid var(--line)" }}>
              {["Gestor", "Carteira", "Saúde", "Custo", "Execuções", "Erro%", "Leads"].map((h, i) => (
                <span key={h} style={{ fontSize: 11, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em", textAlign: i === 0 ? "left" : "right" }}>{h}</span>
              ))}
            </div>
            {isLoading && <div style={{ fontSize: 12, color: "var(--ink-dim)", padding: "12px 0" }}>Carregando...</div>}
            {!isLoading && !data?.managers.length && <div style={{ fontSize: 12, color: "var(--ink-muted)", padding: "12px 0" }}>Nenhum dado</div>}
            {data?.managers.map(m => (
              <div key={m.id} onClick={() => setSelected(m.id)}
                style={{ display: "grid", gridTemplateColumns: "1fr 90px 110px 110px 90px 65px 65px", gap: 8, padding: "10px 0", borderBottom: "1px solid var(--surface-input)", alignItems: "center", cursor: "pointer" }}
                onMouseOver={e => (e.currentTarget.style.background = "var(--surface-input)")}
                onMouseOut={e => (e.currentTarget.style.background = "transparent")}
              >
                <span style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={m.name}>{m.name}</span>
                <span style={{ fontSize: 13, textAlign: "right" }}>
                  <span style={{ fontWeight: 700 }}>{m.agents.total}</span>
                  <span style={{ fontSize: 11, color: "var(--ink-muted)" }}> ({m.agents.active} at.)</span>
                </span>
                <HealthSummary ok={m.health.ok} attention={m.health.attention} zombie={m.health.zombie} />
                <span style={{ fontSize: 13, fontWeight: 700, textAlign: "right" }}>{fmtCost(m.cost_usd)}</span>
                <span style={{ fontSize: 13, textAlign: "right" }}>{fmtInt(m.executions)}</span>
                <span style={{ fontSize: 13, textAlign: "right", color: m.error_rate > 5 ? "var(--danger)" : "var(--ink-dim)" }}>{m.error_rate}%</span>
                <span style={{ fontSize: 13, textAlign: "right", color: "var(--ok)", fontWeight: 600 }}>{fmtInt(m.qualified_leads)}</span>
              </div>
            ))}
          </SectionCard>
        </>
      )}
    </div>
  );
}
